import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import { CheckCircle2, Key, Plus, RefreshCw, Search, Trash2, XCircle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import type { ApiKey } from '../../types';
import { useTranslation } from 'react-i18next';
import { ApiScopeBadge, CapabilityBadge } from './ScopeBadges';
import {
  API_KEY_ROWS_PER_PAGE_OPTIONS,
  ApiKeySortField,
  SortDirection,
  apiKeyEnvironment,
  apiKeyScopeCount,
  apiKeySearchText,
  compareApiKeys,
  displayName,
  formatDate,
  formatOptionalDate,
  formatRateLimit,
} from './utils';

export interface ApiKeysTableProps {
  apiKeys: ApiKey[];
  loading: boolean;
  onCreate: () => void;
  onSelect: (apiKey: ApiKey) => void;
  onRotate: (apiKey: ApiKey) => void;
  onRevoke: (apiKey: ApiKey) => void;
}

export const ApiKeysTable: React.FC<ApiKeysTableProps> = ({
  apiKeys,
  loading,
  onCreate,
  onSelect,
  onRotate,
  onRevoke,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'vi' ? 'vi-VN' : 'en-US';
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ApiKey['status']>('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [sortField, setSortField] = useState<ApiKeySortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const environments = useMemo(
    () =>
      Array.from(
        new Set(apiKeys.map(apiKeyEnvironment).filter((environment) => environment !== '—'))
      ).sort(),
    [apiKeys]
  );

  const filteredApiKeys = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return apiKeys
      .filter((apiKey) => statusFilter === 'all' || apiKey.status === statusFilter)
      .filter(
        (apiKey) => environmentFilter === 'all' || apiKeyEnvironment(apiKey) === environmentFilter
      )
      .filter((apiKey) => !normalizedSearch || apiKeySearchText(apiKey).includes(normalizedSearch))
      .sort((left, right) => compareApiKeys(left, right, sortField, sortDirection));
  }, [apiKeys, environmentFilter, searchTerm, sortDirection, sortField, statusFilter]);

  const pagedApiKeys = useMemo(
    () => filteredApiKeys.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredApiKeys, page, rowsPerPage]
  );

  useEffect(() => {
    setPage(0);
  }, [apiKeys.length, environmentFilter, searchTerm, sortDirection, sortField, statusFilter]);

  const handleSort = (field: ApiKeySortField) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection(
      field === 'name' || field === 'environment' || field === 'status' ? 'asc' : 'desc'
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setEnvironmentFilter('all');
  };

  if (apiKeys.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white py-12 text-center dark:border-slate-700 dark:bg-slate-800">
        <Key className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-slate-500" />
        <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-slate-100">
          {t('apiKeys.empty.title')}
        </h3>
        <p className="mb-6 text-gray-600 dark:text-slate-400">{t('apiKeys.empty.description')}</p>
        <Button variant="contained" startIcon={<Plus size={20} />} onClick={onCreate}>
          {t('apiKeys.create')}
        </Button>
      </div>
    );
  }

  const sortCell = (field: ApiKeySortField, label: string) => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDirection : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <section className="rounded-lg border border-gray-200 bg-white text-gray-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
      <div className="border-b border-gray-200 p-4 dark:border-slate-700">
        <div className="grid gap-3 lg:grid-cols-[minmax(280px,1fr)_180px_180px_auto]">
          <TextField
            fullWidth
            size="small"
            label={t('apiKeys.table.search')}
            placeholder={t('apiKeys.table.searchPlaceholder')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} className="text-gray-400" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label={t('apiKeys.table.statusFilter')}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | ApiKey['status'])}
          >
            <MenuItem value="all">{t('apiKeys.table.allStatuses')}</MenuItem>
            <MenuItem value="ACTIVE">{t('apiKeys.status.ACTIVE')}</MenuItem>
            <MenuItem value="REVOKED">{t('apiKeys.status.REVOKED')}</MenuItem>
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label={t('apiKeys.table.environmentFilter')}
            value={environmentFilter}
            onChange={(event) => setEnvironmentFilter(event.target.value)}
          >
            <MenuItem value="all">{t('apiKeys.table.allEnvironments')}</MenuItem>
            {environments.map((environment) => (
              <MenuItem key={environment} value={environment}>
                {environment}
              </MenuItem>
            ))}
          </TextField>
          <Button type="button" variant="outlined" onClick={clearFilters}>
            {t('apiKeys.table.clearFilters')}
          </Button>
        </div>
        <div className="mt-3 text-xs text-gray-500 dark:text-slate-400">
          {t('apiKeys.table.filteredCount', {
            shown: filteredApiKeys.length,
            total: apiKeys.length,
          })}
        </div>
      </div>

      <TableContainer className="overflow-x-auto">
        <Table
          size="small"
          sx={{
            minWidth: 1180,
            '& .MuiTableCell-root': { color: 'inherit' },
            '& .MuiTableSortLabel-root': { color: 'inherit !important' },
            '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{sortCell('name', t('apiKeys.table.columns.name'))}</TableCell>
              <TableCell>{t('apiKeys.table.columns.prefix')}</TableCell>
              <TableCell>{sortCell('scopes', t('apiKeys.table.columns.scopes'))}</TableCell>
              <TableCell>{sortCell('status', t('apiKeys.table.columns.status'))}</TableCell>
              <TableCell>
                {sortCell('environment', t('apiKeys.table.columns.environment'))}
              </TableCell>
              <TableCell>{sortCell('createdAt', t('apiKeys.table.columns.created'))}</TableCell>
              <TableCell>{sortCell('lastUsed', t('apiKeys.table.columns.lastUsed'))}</TableCell>
              <TableCell>{sortCell('expiresAt', t('apiKeys.table.columns.expires'))}</TableCell>
              <TableCell>{t('apiKeys.table.columns.rateLimit')}</TableCell>
              <TableCell align="right">{t('apiKeys.table.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <div className="flex items-center justify-center py-8">
                    <CircularProgress size={24} />
                  </div>
                </TableCell>
              </TableRow>
            ) : pagedApiKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <div className="py-8 text-center text-sm text-gray-500 dark:text-slate-400">
                    {t('apiKeys.table.noResults')}
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              pagedApiKeys.map((apiKey) => {
                const isActive = apiKey.status === 'ACTIVE';

                return (
                  <TableRow
                    hover
                    key={apiKey.publicId}
                    tabIndex={0}
                    onClick={() => onSelect(apiKey)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') onSelect(apiKey);
                    }}
                    className="cursor-pointer"
                  >
                    <TableCell>
                      <div className="min-w-[190px]">
                        <div className="truncate text-sm font-semibold text-blue-600 dark:text-blue-400">
                          {displayName(apiKey)}
                        </div>
                        <div className="truncate font-mono text-xs text-gray-400 dark:text-slate-500">
                          {apiKey.publicId}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-gray-600 dark:text-slate-300">
                        {apiKey.prefix || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <ApiKeyScopePreview apiKey={apiKey} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`apiKeys.status.${apiKey.status}`, {
                          defaultValue: apiKey.status,
                        })}
                        size="small"
                        color={isActive ? 'success' : 'default'}
                        icon={isActive ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                      />
                    </TableCell>
                    <TableCell>{apiKeyEnvironment(apiKey)}</TableCell>
                    <TableCell>{formatDate(apiKey.createdAt, dateLocale)}</TableCell>
                    <TableCell>{formatOptionalDate(apiKey.lastUsed, dateLocale)}</TableCell>
                    <TableCell>{formatOptionalDate(apiKey.expiresAt, dateLocale)}</TableCell>
                    <TableCell>{formatRateLimit(apiKey)}</TableCell>
                    <TableCell align="right">
                      {isActive && (
                        <div className="flex justify-end gap-1">
                          <Tooltip title={t('apiKeys.card.rotate')} arrow>
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                onRotate(apiKey);
                              }}
                              color="primary"
                              size="small"
                            >
                              <RefreshCw size={18} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('apiKeys.revoke.revokeKey')} arrow>
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                onRevoke(apiKey);
                              }}
                              color="error"
                              size="small"
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        className="text-gray-700 dark:text-slate-300"
        component="div"
        count={filteredApiKeys.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={API_KEY_ROWS_PER_PAGE_OPTIONS}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        labelRowsPerPage={t('apiKeys.table.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          t('apiKeys.table.displayedRows', { from, to, count })
        }
        sx={{
          color: 'inherit',
          '& .MuiTablePagination-selectLabel': { color: 'inherit' },
          '& .MuiTablePagination-displayedRows': { color: 'inherit' },
          '& .MuiSvgIcon-root': { color: 'inherit' },
        }}
      />
    </section>
  );
};

export const ApiKeyScopePreview: React.FC<{ apiKey: ApiKey }> = ({ apiKey }) => {
  const { t } = useTranslation();
  const scopeCount = apiKeyScopeCount(apiKey);
  const visibleLimit = 2;

  if (scopeCount === 0) {
    return (
      <span className="text-sm text-gray-400 dark:text-slate-500">
        {t('apiKeys.card.noCapabilities')}
      </span>
    );
  }

  const visibleScopes = apiKey.scopes?.slice(0, visibleLimit);
  const visibleCapabilities = apiKey.scopes?.length
    ? []
    : apiKey.capabilities.slice(0, visibleLimit);
  const remaining = Math.max(0, scopeCount - visibleLimit);

  return (
    <div className="flex min-w-[220px] max-w-[320px] flex-wrap gap-1.5">
      {visibleScopes?.map((scope) => (
        <ApiScopeBadge
          key={`${scope.scope}:${scope.resourceType ?? ''}:${scope.resourceId ?? ''}`}
          scope={scope}
        />
      ))}
      {visibleCapabilities.map((capability) => (
        <CapabilityBadge key={capability.capabilityCode} scope={capability} />
      ))}
      {remaining > 0 && (
        <Chip
          label={t('apiKeys.table.moreScopes', { count: remaining })}
          size="small"
          variant="outlined"
          className="!text-xs"
        />
      )}
    </div>
  );
};
